// BlogDetail.jsx - SEO-Optimized Blog Detail Page
"use client";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowLeft, ArrowRight, Loader2, Tag } from "lucide-react";
import { NavbarDemo } from "./NavBar";
import SEOHead from "./SEOHead";
import {
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateBlogPostingSchema,
  generateFAQSchema,
} from "../utils/seo";
import { Button } from "./ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import api from "../lib/api";
import { BLOG_PROSE_CLASS } from "../utils/blogProse";

export default function BlogDetail() {
  const { id, blogdid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);

  // Get the article ID from either 'id' or 'blogdid' param, or from pathname for static blog
  const isStaticBlogRoute =
    location.pathname === "/how-to-find-a-good-massage-therapist-home-visits";
  const articleId =
    id ||
    blogdid ||
    (isStaticBlogRoute
      ? "how-to-find-a-good-massage-therapist-home-visits"
      : null);

  // Fetch article details
  useEffect(() => {
    const fetchArticle = async () => {
      if (!articleId) {
        setError("Article ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await api.blog.getArticleById(articleId);
        setArticle(data);
        setError(null);

        // Fetch related articles
        try {
          const allArticles = await api.blog.getAllArticles();
          const related = allArticles
            .filter((a) => String(a.id) !== String(articleId))
            .slice(0, 3);
          setRelatedArticles(related);
        } catch (err) {
          console.error("Error fetching related articles:", err);
        }
      } catch (err) {
        console.error("Error fetching blog article:", err);
        setError("Failed to load article. Please try again later.");
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [articleId]);

  // Generate breadcrumbs
  const isStaticBlog = article?.isStatic || isStaticBlogRoute;
  const breadcrumbs = [
    { name: "Home", url: "https://bookatherapy.com" },
    ...(isStaticBlog
      ? [
          {
            name: article?.title || "How to Find a Good Massage Therapist",
            url: "https://bookatherapy.com/how-to-find-a-good-massage-therapist-home-visits",
          },
        ]
      : [
          { name: "Blog", url: "https://bookatherapy.com/blog" },
          ...(article
            ? [
                {
                  name: article.title,
                  url: `https://bookatherapy.com/blog/${article.id}`,
                },
              ]
            : []),
        ]),
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavbarDemo />
        <section className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#98C454] mx-auto mb-4" />
            <p className="text-gray-600">Loading article...</p>
          </div>
        </section>
      </div>
    );
  }

  // Error state
  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavbarDemo />
        <section className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-red-50 border border-red-200 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-red-800 mb-4">
                Article Not Found
              </h3>
              <p className="text-red-600 mb-6">
                {error || "The article you're looking for doesn't exist."}
              </p>
              <Button
                onClick={() => navigate(isStaticBlogRoute ? "/" : "/blog")}
                className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F]">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {isStaticBlogRoute ? "Back to Home" : "Back to Blog"}
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={`${article.title} - BookaTherapy`}
        description={article.meta_description || article.title}
        keywords={article.keywords?.join(", ") || "massage therapy, wellness"}
        canonical={
          isStaticBlog
            ? "https://bookatherapy.com/how-to-find-a-good-massage-therapist-home-visits"
            : `https://bookatherapy.com/blog/${article.id}`
        }
        ogType="article"
        ogImage={
          article.hero_image_url ||
          "https://bookatherapy.com/static/media/logo2.fbfb04de82c528272816.png"
        }
        structuredData={[
          // Use BlogPosting schema for static blog, Article schema for regular blogs
          isStaticBlog
            ? generateBlogPostingSchema({
                headline: article.title,
                description:
                  article.meta_description ||
                  "Learn how to find a trustworthy massage therapist offering home visits in Canada, with tips for safe, effective sessions.",
                image:
                  article.hero_image_url ||
                  "https://bookatherapy.com/static/media/logo2.fbfb04de82c528272816.png",
                url: "https://bookatherapy.com/how-to-find-a-good-massage-therapist-home-visits",
                datePublished: article.created_at || "2025-12-05",
                dateModified: article.created_at || "2025-12-05",
                articleBody:
                  article.content_html
                    ?.replace(/<[^>]*>/g, " ")
                    .replace(/\s+/g, " ")
                    .trim()
                    .substring(0, 500) ||
                  "In today's fast-paced world, finding time for self-care can feel like a luxury. Mobile massage therapy delivers professional relief to your doorstep, easing stress and tension. Learn how to choose a licensed, experienced therapist for safe and effective home sessions in Canada.",
                keywords:
                  article.keywords?.join(", ") ||
                  "home massage therapist, mobile massage Canada, RMT home visits, massage therapy at home",
              })
            : generateArticleSchema({
                title: article.title,
                description: article.meta_description || article.title,
                image: article.hero_image_url,
                publishedDate: article.created_at,
                url: `https://bookatherapy.com/blog/${article.id}`,
              }),
          generateBreadcrumbSchema(breadcrumbs),
          // Add FAQ schema if FAQs exist (for static blog)
          ...(isStaticBlog && article.faqs && article.faqs.length > 0
            ? [generateFAQSchema(article.faqs)]
            : []),
        ]}
      />

      <NavbarDemo />

      {/* Back to Blog Button */}
      <section className="pt-24 pb-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={() => navigate(isStaticBlog ? "/" : "/blog")}
            className="text-[#252F4F] hover:text-[#98C454]">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isStaticBlog ? "Back to Home" : "Back to Blog"}
          </Button>
        </div>
      </section>

      {/* Article Header */}
      <article className="pb-16 bg-white overflow-x-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="min-w-0">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#252F4F] mb-6 break-words">
              {article.title}
            </h1>

            {/* Meta Information */}
            <div className="flex items-center gap-4 text-gray-600 mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>
                  {new Date(article.created_at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
              {article.languageCode && (
                <>
                  <span>•</span>
                  <span className="uppercase">{article.languageCode}</span>
                </>
              )}
            </div>

            {/* Keywords/Tags */}
            {article.keywords && article.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                <Tag className="h-5 w-5 text-gray-500" />
                {article.keywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="bg-[#98C454]/10 text-[#252F4F] px-3 py-1 rounded-full text-sm font-medium">
                    {keyword}
                  </span>
                ))}
              </div>
            )}

            {/* Cover / hero image */}
            {(article.hero_image_url || article.cover_image_url) && (
              <img
                src={article.hero_image_url || article.cover_image_url}
                alt={article.title}
                className="w-full max-h-[460px] object-cover rounded-2xl shadow-md mb-10"
                loading="eager"
              />
            )}

            {/* Article Content - HTML */}
            {article.content_html &&
              (() => {
                // Remove the first h1 tag from content_html to avoid duplicate title
                // (title is already displayed above)
                let cleanedContent = article.content_html;
                if (!isStaticBlog) {
                  // Remove first h1 tag and its content for dynamic blogs
                  cleanedContent = cleanedContent.replace(
                    /<h1[^>]*>.*?<\/h1>/i,
                    "",
                  );
                }
                return (
                  <div
                    className={BLOG_PROSE_CLASS}
                    dangerouslySetInnerHTML={{ __html: cleanedContent }}
                  />
                );
              })()}

            {/* FAQs Section with Accordion */}
            {article.faqs && article.faqs.length > 0 && (
              <div className="mt-12">
                <h2 className="text-3xl font-bold text-[#252F4F] mb-6">FAQs</h2>
                <Accordion type="single" collapsible className="w-full">
                  {article.faqs.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`faq-${index}`}
                      className="border-b border-gray-200">
                      <AccordionTrigger className="text-left text-lg font-semibold text-[#252F4F] hover:text-[#98C454] py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-700 leading-relaxed pt-2 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* Seed Keyword */}
            {article.seedKeyword && (
              <div className="mt-12 p-6 bg-gray-100 rounded-xl">
                <p className="text-sm text-gray-600">
                  <strong>Primary Topic:</strong> {article.seedKeyword}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#252F4F] mb-8">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle, index) => (
                <motion.article
                  key={relatedArticle.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  onClick={() => navigate(`/blog/${relatedArticle.id}`)}
                  className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#252F4F] mb-2 group-hover:text-[#98C454] transition-colors">
                      {relatedArticle.title}
                    </h3>

                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-4">
                      <Calendar className="h-3 w-3" />
                      {new Date(relatedArticle.created_at).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                        },
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#252F4F] to-[#1e2640]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Experience Professional Massage Therapy?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Book your personalized massage session today
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/book")}
              className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-10 py-6 text-lg rounded-xl font-semibold">
              Book Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
